import React, { useState, useEffect, useCallback } from 'react';
import { rbacApi, usersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Plus, Trash, Pencil, Shield, Users, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PROCUREMENT_SECTORS } from '../lib/utils';

const ACTION_LABELS = { view: 'View', create: 'Create', update: 'Update', delete: 'Delete', export: 'Export', import: 'Import' };
const MODULE_LABELS = {
  dashboard: 'Dashboard', contacts: 'Contacts', companies: 'Companies', deals: 'Deals',
  tasks: 'Tasks', documents: 'Documents', procurement: 'Procurement', workflows: 'Workflows',
  bi: 'Intelligence', marketplace: 'Marketplace', affiliate: 'Affiliate', reports: 'Reports',
  calendar: 'Calendar', custom_fields: 'Custom Fields', audit_trail: 'Audit Trail',
  ai_assistant: 'AI Assistant', notifications: 'Notifications', settings: 'Settings'
};

export default function RBACPage() {
  const [permSets, setPermSets] = useState([]);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assignUser, setAssignUser] = useState(null);
  const [selectedSetId, setSelectedSetId] = useState('');
  const [formData, setFormData] = useState({ name: '', description: '', department: '', permissions: {} });

  const fetchData = useCallback(async () => {
    try {
      const [setsRes, usersRes, modRes] = await Promise.all([
        rbacApi.getPermissionSets(),
        usersApi.getAll({ limit: 100 }),
        rbacApi.getModules()
      ]);
      setPermSets(setsRes.data);
      setUsers(usersRes.data);
      setModules(modRes.data.modules);
      setActions(modRes.data.actions);
    } catch (error) {
      toast.error('Failed to load RBAC data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await rbacApi.updatePermissionSet(editing.id, formData);
        toast.success('Permission set updated');
      } else {
        await rbacApi.createPermissionSet(formData);
        toast.success('Permission set created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this permission set?')) return;
    try { await rbacApi.deletePermissionSet(id); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleAssign = async () => {
    if (!assignUser || !selectedSetId) return;
    try {
      await rbacApi.assignUserPermissions(assignUser.id, { permission_set_id: selectedSetId });
      toast.success(`Permissions assigned to ${assignUser.name}`);
      setIsAssignOpen(false);
      setAssignUser(null);
      setSelectedSetId('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (ps) => {
    setEditing(ps);
    setFormData({ name: ps.name, description: ps.description || '', department: ps.department || '', permissions: ps.permissions || {} });
    setIsDialogOpen(true);
  };

  const resetForm = () => { setEditing(null); setFormData({ name: '', description: '', department: '', permissions: {} }); };

  const togglePermission = (module, action) => {
    setFormData(prev => {
      const perms = { ...prev.permissions };
      if (!perms[module]) perms[module] = [];
      if (perms[module].includes(action)) {
        perms[module] = perms[module].filter(a => a !== action);
        if (perms[module].length === 0) delete perms[module];
      } else {
        perms[module] = [...perms[module], action];
      }
      return { ...prev, permissions: perms };
    });
  };

  const selectAllModule = (module) => {
    setFormData(prev => {
      const perms = { ...prev.permissions };
      perms[module] = [...actions];
      return { ...prev, permissions: perms };
    });
  };

  const deselectAllModule = (module) => {
    setFormData(prev => {
      const perms = { ...prev.permissions };
      delete perms[module];
      return { ...prev, permissions: perms };
    });
  };

  const getPermCount = (perms) => Object.values(perms || {}).reduce((sum, acts) => sum + acts.length, 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6" data-testid="rbac-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Access Control</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage permissions per module, action & department</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="rounded-sm" data-testid="add-perm-set-btn">
          <Plus size={18} className="mr-2" /> New Permission Set
        </Button>
      </div>

      <Tabs defaultValue="sets">
        <TabsList>
          <TabsTrigger value="sets"><Shield size={16} className="mr-1" /> Permission Sets</TabsTrigger>
          <TabsTrigger value="users"><Users size={16} className="mr-1" /> User Assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="sets" className="space-y-4">
          {permSets.length === 0 ? (
            <div className="text-center p-12 border border-dashed border-border rounded-sm">
              <Shield size={40} className="mx-auto text-muted-foreground mb-3 opacity-40" />
              <p className="text-muted-foreground">No permission sets created</p>
              <p className="text-xs text-muted-foreground mt-1">Create sets to assign granular permissions per department</p>
            </div>
          ) : permSets.map(ps => (
            <div key={ps.id} className="bg-card border border-border p-4 rounded-sm" data-testid={`perm-set-${ps.id}`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <ShieldCheck size={18} className="text-primary" />
                    <h3 className="font-semibold">{ps.name}</h3>
                    {ps.department && <Badge variant="outline">{ps.department}</Badge>}
                    <Badge variant="secondary" className="text-xs">{getPermCount(ps.permissions)} permissions</Badge>
                  </div>
                  {ps.description && <p className="text-sm text-muted-foreground mt-1">{ps.description}</p>}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {Object.keys(ps.permissions || {}).map(mod => (
                      <Badge key={mod} variant="outline" className="text-xs">{MODULE_LABELS[mod] || mod}: {ps.permissions[mod]?.join(', ')}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(ps)}><Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(ps.id)}><Trash size={14} /></Button>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users">
          <div className="bg-card border border-border rounded-sm overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-border bg-muted/50">
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">User</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Role</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Department</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Permission Set</th>
                <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
              </tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-border hover:bg-muted/30">
                    <td className="p-3 font-medium">{u.name}<br /><span className="text-xs text-muted-foreground">{u.email}</span></td>
                    <td className="p-3"><Badge className={u.role === 'admin' ? 'bg-destructive text-white' : u.role === 'manager' ? 'bg-primary text-white' : 'bg-muted'}>{u.role}</Badge></td>
                    <td className="p-3 text-sm">{u.department || '-'}</td>
                    <td className="p-3 text-sm">{permSets.find(p => p.id === u.permission_set_id)?.name || <span className="text-muted-foreground">None</span>}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => { setAssignUser(u); setSelectedSetId(u.permission_set_id || ''); setIsAssignOpen(true); }} data-testid={`assign-${u.id}`}>
                        Assign
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Permission Set */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Permission Set' : 'New Permission Set'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="perm-name" />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={formData.department || "none"} onValueChange={(v) => setFormData({ ...formData, department: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="Sales">Sales</SelectItem>
                    <SelectItem value="Marketing">Marketing</SelectItem>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="Engineering">Engineering</SelectItem>
                    {PROCUREMENT_SECTORS.map(s => <SelectItem key={s.id} value={s.label}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label className="mb-3 block">Module Permissions</Label>
              <div className="border border-border rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-2 font-semibold">Module</th>
                    {actions.map(a => <th key={a} className="text-center p-2 font-semibold text-xs">{ACTION_LABELS[a]}</th>)}
                    <th className="text-center p-2 font-semibold text-xs">All</th>
                  </tr></thead>
                  <tbody>
                    {modules.map(mod => (
                      <tr key={mod} className="border-b border-border">
                        <td className="p-2 font-medium">{MODULE_LABELS[mod] || mod}</td>
                        {actions.map(act => (
                          <td key={act} className="text-center p-2">
                            <Checkbox
                              checked={formData.permissions[mod]?.includes(act) || false}
                              onCheckedChange={() => togglePermission(mod, act)}
                            />
                          </td>
                        ))}
                        <td className="text-center p-2">
                          <Button type="button" variant="ghost" size="sm" className="h-6 text-xs"
                            onClick={() => formData.permissions[mod]?.length === actions.length ? deselectAllModule(mod) : selectAllModule(mod)}>
                            {formData.permissions[mod]?.length === actions.length ? 'Clear' : 'All'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{getPermCount(formData.permissions)} permissions selected</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="perm-submit">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Permission Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Assign Permissions</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {assignUser && <p className="text-sm">Assigning to: <strong>{assignUser.name}</strong> ({assignUser.email})</p>}
            <div className="space-y-2">
              <Label>Permission Set</Label>
              <Select value={selectedSetId || "none"} onValueChange={(v) => setSelectedSetId(v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select permission set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (default role-based)</SelectItem>
                  {permSets.map(ps => <SelectItem key={ps.id} value={ps.id}>{ps.name} {ps.department ? `(${ps.department})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignOpen(false)}>Cancel</Button>
            <Button onClick={handleAssign} data-testid="assign-submit">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
